import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from 'react'
import {
  AcademyEvent,
  AcademyVerified,
} from '../../Api/Events/Academy/academyEvents.types'
import {
  fetchAcademyVerified,
  fetchActiveAcademyEvents,
} from '../../Api/Events/Academy/events.api'
import { SelectListItem } from '../../Components/Layout/Selectors/General/GeneralSelect'
import { getFullNameByCode } from '../../Utils'
import { STORAGE_NAMES } from '../../Constants'
import { StorageContext } from '../StorageContext'

type AcademyContextType = {
  academyEvents: AcademyEvent[]
  verifiedClubs: AcademyVerified[]
  stateList: SelectListItem[]
  monthList: SelectListItem[]
  trainingList: SelectListItem[]
  stateSelect: string
  monthSelect: string
  trainingSelect: string
  filteredList: AcademyEvent[]
  loading: boolean
  verifiedLoading: boolean
  handleFilterChange: (filterType: string, value: string) => void
  handleReset: () => void
  getGameWeekendEvents: (market: string) => AcademyEvent | undefined
  returnFilteredEvents: (filterObj: AcademyFilterObject) => AcademyEvent[]
  applyLocationFromUrl: () => void
}

type ParticipantType = {
  id: number
  label: string
  value: string
}

export type AcademyFilterObject = {
  participant?: string
  state?: string
  month?: string
  training?: string
}

export const AcademyContext = createContext<AcademyContextType | null>(null)

export const participantTypes: ParticipantType[] = [
  { id: 0, label: 'All', value: 'all' },
  { id: 1, label: 'Boys', value: 'boys' },
  { id: 2, label: 'Girls', value: 'girls' },
]

export const AcademyProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { isStorageReady, store, getStoredData } = useContext(StorageContext)!
  const [academyEvents, setAcademyEvents] = useState<AcademyEvent[]>([])
  const [filteredList, setFilteredList] = useState<AcademyEvent[]>([])
  const [participantSelect, setParticipantSelect] = useState<ParticipantType>(
    participantTypes[0]
  )
  const [monthSelect, setMonthSelect] = useState<string>('By Month')
  const [stateSelect, setStateSelect] = useState<string>('All Locations')
  const [trainingSelect, setTrainingSelect] = useState<string>('All Training')
  const [stateList, setStateList] = useState<SelectListItem[]>([])
  const [monthList, setMonthList] = useState<SelectListItem[]>([])
  const [trainingList, setTrainingList] = useState<SelectListItem[]>([])
  const [verifiedClubs, setVerifiedClubs] = useState<AcademyVerified[]>([])
  const locationFilterApplied = useRef<boolean>(false)

  const [loading, setLoading] = useState<boolean>(true)
  const [verifiedLoading, setVerifiedLoading] = useState<boolean>(true)

  useEffect(() => {
    if (isStorageReady) {
      fetchAcademyData()
    }
  }, [isStorageReady])

  const filterEvents = useCallback(
    (participant: string, state: string, month: string, training: string) => {
      const filtered = academyEvents.filter(
        (ev) =>
          (participant === 'all'
            ? ev?.boysOrGirls !== ''
            : ev.boysOrGirls.toLowerCase() === participant ||
              ev.boysOrGirls === 'Both') &&
          (state === '' || state === 'All Locations'
            ? ev.state !== ''
            : getFullNameByCode(ev.state) === state) &&
          (month === '' || month === 'By Month'
            ? ev.formattedEventDate !== '' && ev.formattedEventDate !== null
            : ev.formattedEventDate.slice(0, 3) === month) &&
          (training === '' || training === 'All Training'
            ? ev.eventType !== ''
            : ev.eventType === training)
      )
      setFilteredList(filtered)
      return filtered
    },
    [academyEvents]
  )

  // Apply URL location filter only once after data is loaded
  useEffect(() => {
    if (
      academyEvents.length > 0 &&
      stateList.length > 0 &&
      !locationFilterApplied.current
    ) {
      applyLocationFromUrl()
      locationFilterApplied.current = true
    }
  }, [academyEvents, stateList])

  const findMatchingState = useCallback(
    (locationParam: string): SelectListItem | undefined => {
      const normalizedLocation = locationParam.toLowerCase()

      return stateList.find(
        (state) =>
          String(state.value).toLowerCase() === normalizedLocation ||
          state.label.toLowerCase() === normalizedLocation
      )
    },
    [stateList]
  )

  const applyStateFilter = useCallback(
    (stateLabel: string): void => {
      setStateSelect(stateLabel)
      filterEvents(participantSelect.value, stateLabel, monthSelect, trainingSelect)
    },
    [filterEvents, participantSelect.value, monthSelect, trainingSelect]
  )

  const applyLocationFromUrl = useCallback((): void => {
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const locationParam = urlParams.get('location')

      if (!locationParam) return

      const matchingState = findMatchingState(locationParam)

      if (matchingState) {
        applyStateFilter(matchingState.label)
      }
    } catch (error) {
      console.error('Error applying location from URL:', error)
    }
  }, [findMatchingState, applyStateFilter])

  const fetchAcademyData = async () => {
    getStoredPlayData()
    const [events, verified] = await Promise.all([
      getAcademyEvents(),
      getVerifiedClubs(),
    ])

    //Wait for both, then cache
    if (events.length > 0 && verified.length > 0) {
      cacheNewPlayData(events, verified)
    }
  }

  const getAcademyEvents = async () => {
    const acEvents = await fetchActiveAcademyEvents()
    if (acEvents && acEvents.length > 0) {
      let sorted = acEvents.sort(
        (a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime()
      )
      setAcademyEvents(sorted)
      setFilteredList(sorted)
      createDropdowns(sorted)
      setLoading(false)
      return sorted
    }
    setLoading(false)
    return []
  }

  const getVerifiedClubs = async () => {
    const clubs = await fetchAcademyVerified()
    if (clubs && clubs.length > 0) {
      let sorted = clubs.sort((a, b) => (a.location > b.location ? 1 : -1))
      setVerifiedClubs(sorted)
      setVerifiedLoading(false)
      return sorted
    }
    setVerifiedLoading(false)
    return []
  }

  const getStoredPlayData = async (): Promise<void> => {
    const storedData = await getStoredData(STORAGE_NAMES.pll_play)
    if (storedData?.data?.events) {
      setAcademyEvents(storedData.data.events ?? [])
      setLoading(false)
    }

    if (storedData?.data?.verifiedClubs) {
      setVerifiedClubs(storedData.data.verifiedClubs ?? [])
      setVerifiedLoading(false)
    }
  }

  const cacheNewPlayData = async (
    evs: AcademyEvent[] = [],
    verifiedClubs: AcademyVerified[] = []
  ): Promise<void> => {
    const dataToStore = {
      events: evs,
      verifiedClubs,
    }
    store(STORAGE_NAMES.pll_play, dataToStore)
  }

  const getGameWeekendEvents = (market: string) => {
    if (!academyEvents || academyEvents.length === 0) return
    let gameEv = academyEvents.find(
      (ev) => ev.gameWeekendEvent && ev.market && ev.market.toLowerCase() === market
    )
    return gameEv
  }

  const createDropdowns = async (acEvs: AcademyEvent[]) => {
    if (acEvs.length > 0) {
      let locations = createStateList(acEvs)
      setStateList(locations)
      let months = createMonthList(acEvs)
      setMonthList(months)
      let trainings = createTrainingList(acEvs)
      setTrainingList(trainings)
    }
    setLoading(false)
    return
  }

  const createStateList = (events: AcademyEvent[]) => {
    let stateDropdown = events
      .reduce((accumulator: SelectListItem[], event) => {
        const stateLabel = getFullNameByCode(event.state) || event.state
        if (!accumulator.some((state) => state.label === stateLabel)) {
          accumulator.push({ label: stateLabel, value: event.state })
        }
        return accumulator
      }, [])
      .sort((a, b) => (a.label > b.label ? 1 : -1))
    return [{ label: 'All Locations', value: 'All Locations' }, ...stateDropdown]
  }

  const createMonthList = (events: AcademyEvent[]) => {
    let l = events.reduce(
      (accumulator: SelectListItem[], event) => {
        if (!event.formattedEventDate) {
          return accumulator
        }
        const monthLabel = event.formattedEventDate.slice(0, 3)
        if (!accumulator.some((mo) => mo.label === monthLabel)) {
          accumulator.push({ label: monthLabel, value: monthLabel })
        }
        return accumulator
      },
      [{ label: 'By Month', value: 'By Month' }]
    )
    return l
  }

  const createTrainingList = (events: AcademyEvent[]) => {
    let l = events.reduce(
      (accumulator: SelectListItem[], event) => {
        if (!event.formattedEventDate) {
          return accumulator
        }
        const trainingLabel = event.eventType
        if (!accumulator.some((tr) => tr.label === trainingLabel)) {
          accumulator.push({ label: trainingLabel, value: trainingLabel })
        }
        return accumulator
      },
      [{ label: 'All Training', value: 'All Training' }]
    )
    return l
  }

  const handleFilterChange = useCallback(
    (filterType: string, value: string) => {
      switch (filterType) {
        case 'participant':
          if (value === 'all') {
            setParticipantSelect(participantTypes[0])
            filterEvents('all', stateSelect, monthSelect, trainingSelect)
            return
          }
          let foundParticipant = participantTypes.find((type) => type.value === value)
          if (foundParticipant) {
            setParticipantSelect(foundParticipant)
            filterEvents(
              foundParticipant.value,
              stateSelect,
              monthSelect,
              trainingSelect
            )
          }
          break
        case 'state':
          setStateSelect(value)
          filterEvents(participantSelect.value, value, monthSelect, trainingSelect)
          break
        case 'month':
          setMonthSelect(value)
          filterEvents(participantSelect.value, stateSelect, value, trainingSelect)
          break
        case 'training':
          setTrainingSelect(value)
          filterEvents(participantSelect.value, stateSelect, monthSelect, value)
          break
        default:
          break
      }
    },
    [filterEvents, stateSelect, monthSelect, trainingSelect, participantSelect]
  )

  const returnFilteredEvents = useCallback(
    (filterObj: AcademyFilterObject) => {
      return filterEvents(
        filterObj?.participant || 'all',
        filterObj?.state || 'All Locations',
        filterObj?.month || 'By Month',
        filterObj?.training || 'All Training'
      )
    },
    [filterEvents]
  )

  const handleReset = useCallback(() => {
    setStateSelect('All Locations')
    setMonthSelect('By Month')
    setTrainingSelect('All Training')
    filterEvents(participantSelect.value, 'All Locations', 'By Month', 'All Training')
  }, [filterEvents, participantSelect.value])

  return (
    <AcademyContext.Provider
      value={{
        academyEvents,
        verifiedClubs,
        filteredList,
        stateList,
        stateSelect,
        monthList,
        monthSelect,
        trainingList,
        trainingSelect,
        loading,
        verifiedLoading,
        handleFilterChange,
        handleReset,
        getGameWeekendEvents,
        returnFilteredEvents,
        applyLocationFromUrl,
      }}
    >
      {children}
    </AcademyContext.Provider>
  )
}